import { BehaviorSubject, Subscription, timer } from "rxjs";
import { ApiClient, OfferEntry, SaveBetslipItem, TicketDetailData } from "./ApiClient";

export enum AppStateUserTokenState {
  NotLogged,
  Loading,
  Logged
}
export type AppStateUserToken = {
  state: AppStateUserTokenState,
  token?: string;
}

export class AppState {
  private static instances: { [key: string]: AppState } = {};
  private widgetCount: number = 0;
  private coundownSub: Subscription | null = null;
  private subscriptions = new Subscription();
  public launchedUserToken = new BehaviorSubject<AppStateUserToken>({state: AppStateUserTokenState.NotLogged});

  public selectedNumbers = new BehaviorSubject<Array<number>>([]);
  public tips = new BehaviorSubject<Array<LotteryTip>>([]);
  public nextOffer = new BehaviorSubject<OfferEntry | null>(null);
  public showingOffer = new BehaviorSubject<OfferEntry | null>(null);
  public servetTimeDiffMs : number = 0;
  public offerTimeToMs = new BehaviorSubject<number | null>(null);
  public lastSavedBetslipTime = new BehaviorSubject<number | null>(null);
  public serverVersion = new BehaviorSubject<string | null>(null);

  public selectRoundCount = new BehaviorSubject<number | null>(null);
  private reloadingOffer: ReturnType<typeof setTimeout> | null = null;

  public myTickets = new BehaviorSubject<Array<TicketDetailData>>([]);
  public winNumbersMyTickets = new BehaviorSubject<Array<number>>([]);
  public roundFinished = new BehaviorSubject<boolean>(false);

  public loaders = {
    betslipSending: new BehaviorSubject<boolean>(false)
  }

  public api = ApiClient.getInstance();

  private constructor(public gameIdent: string, userToken: BehaviorSubject<string | null>) { 
    this.reloadNextOffer();
    this.subscriptions.add(this.myTickets.subscribe(tickets => {
      this.roundFinished.next(false);
      this.winNumbersMyTickets.next([]);
    }));
    this.subscriptions.add(userToken.subscribe(token => {
      if(token == null){
        this.launchedUserToken.next({state: AppStateUserTokenState.NotLogged});
      } else {
        this.launchedUserToken.next({state: AppStateUserTokenState.Loading});
        this.api.launch({token: token}).then(resp => {
          if(resp.status.toLowerCase() == 'ok'){
            this.launchedUserToken.next({state: AppStateUserTokenState.Logged, token: resp.token});
            this.api.setToken(resp.token);
          } else {
            this.launchedUserToken.next({state: AppStateUserTokenState.NotLogged});
          }
        }).catch(() => {
          this.launchedUserToken.next({state: AppStateUserTokenState.NotLogged});
        });
      }
    }));
    this.subscriptions.add(this.nextOffer.subscribe(nextOffer => {
      this.coundownSub?.unsubscribe();
      if(!nextOffer){
        this.offerTimeToMs.next(null);
        return;
      }
      this.updateOfferTimeTo(nextOffer);
      const delay = 1000 - new Date().getMilliseconds();
      this.coundownSub = timer(delay).subscribe(() => {
        this.coundownSub?.unsubscribe();
        this.updateOfferTimeTo(nextOffer);
        this.coundownSub = timer(0, 1000).subscribe(() => {
          this.updateOfferTimeTo(nextOffer);
        });
      });
    }));
    this.manageTipsStorage();
  }

  private manageTipsStorage(){
    if(this.gameIdent){
      let key = this.gameIdent + "_tips";
      let keyValidTo = this.gameIdent + "_valid_to";
      let valueFromStorage = localStorage.getItem(key);
      let validTo = localStorage.getItem(keyValidTo);
      if(valueFromStorage && validTo){
        const validToTimeStamp = Number(validTo);
        if(validToTimeStamp > Date.now()){
          this.tips.next(JSON.parse(valueFromStorage) as Array<LotteryTip>);
        }
      }
      this.subscriptions.add(this.tips.subscribe(tips => {
        localStorage.setItem(key, JSON.stringify(tips));
        localStorage.setItem(keyValidTo, (Date.now() + (1000 * 60 * 30)).toString()); //30 minutes valid
      }));
    }
  }

  updateOfferTimeTo(nextOffer: OfferEntry) {
    const now = new Date();
    const to = new Date(nextOffer.event_end_at);
    const diff = Math.max(0, to.getTime() - now.getTime() + this.servetTimeDiffMs);
    this.offerTimeToMs.next(diff);
    if(diff == 0){
      this.coundownSub?.unsubscribe();
      if(this.reloadingOffer == null){
        this.reloadingOffer = setTimeout(() => {
          this.reloadNextOffer();
        }, 10000);
      }
    }
  }

  static getInstance(gameIdent: string, userToken: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null)): AppState {
    if (!AppState.instances[gameIdent]) 
      AppState.instances[gameIdent] = new AppState(gameIdent, userToken);
    return AppState.instances[gameIdent];
  }

  public destroy() {
    if(AppState.instances[this.gameIdent]){
      AppState.instances[this.gameIdent].coundownSub?.unsubscribe();
      AppState.instances[this.gameIdent].subscriptions.unsubscribe();
      const reloadOfferTimeout = AppState.instances[this.gameIdent].reloadingOffer;
      if(reloadOfferTimeout != null)
        clearTimeout(reloadOfferTimeout);
      delete(AppState.instances[this.gameIdent]);
    }
  }

  public reloadNextOffer(){
    if(this.gameIdent == "undefined"){
      this.reloadingOffer = null;
      return;
    }
    this.nextOffer.next(null);
    this.api.offer({
      ident: this.gameIdent
    }).then(value => {
      if(value.server_time)
        this.servetTimeDiffMs = new Date().getTime() - new Date(value.server_time).getTime();
      if(value.offer.length > 0){
        this.nextOffer.next(value.offer[0]);
      }
    }).finally(() => {
      this.reloadingOffer = null;
    });
  }

  public saveBetslip(stake: number, repeat: number, tips: Array<LotteryTip>){
    if(stake <= 0)
      return Promise.reject(new Error("stake have to be bigger than zero"));

    const offer = this.nextOffer.getValue();
    if(!offer)
      return Promise.reject(new Error("nextOffer not exists"));

    const betslipItems: Array<SaveBetslipItem> = [];
    tips.forEach(tip => {
      if(tip.readyToSend)
        betslipItems.push({
          ident: this.gameIdent,
          id_offer: offer.id_offer,
          repeat: repeat,
          bets: [
            "numbers" in tip ?
              {
                type: "NumberBet",
                numbers: tip.numbers
              }
            :
              {
                type: "NumberCombiBet",
                columns: tip.columns
              }
          ]
        });
    });
    if(betslipItems.length == 0)
      return Promise.reject(new Error("betslip items ready to send not exists"));
    var token = this.launchedUserToken.getValue().token;
    if(token == null)
      return Promise.reject(new Error("User not logged, please call launch with user token"));
    this.loaders.betslipSending.next(true);
    return this.api.saveBetslip({
      token: token,
      betslip: {
        stake: stake,
        items: betslipItems
      }
    }, (resp, error) => {
      this.loaders.betslipSending.next(false);
    });
  }

  incrementWidgetCount(): void {
    this.widgetCount++;
  }

  decrementWidgetCount(): void {
    if (this.widgetCount > 0) this.widgetCount--;
  }

  getWidgetCount(): number {
    return this.widgetCount;
  }

}

export type LotteryTip = LotteryTipNormal | LotteryTipCombi;
export interface LotteryTipNormal {
  numbers: Array<number>;
  type: "normal" | "most" | "less",
  showMostDrawn: boolean,
  showLeastDrawn: boolean,
  readyToSend: boolean
}

export interface LotteryTipCombi {
  type: "normal" | "most" | "less",
  columns: Array<Array<number>>,
  readyToSend: boolean,
  combinations: Array<string>
}